// ── Replay Storage ──
// Save/load replays to localStorage, download as files, and import from files.
// Handles serialization of Map types (JSON doesn't support Map natively).

import { type ReplayData, type ReplayFrame } from './replay-recorder';

const REPLAY_KEY_PREFIX = 'replay_';
const REPLAY_INDEX_KEY = 'replay_index';
const REPLAY_VERSION = 1;

/** Hard cap for imported replay files. Above this we refuse to parse to avoid
 *  blocking the main thread on JSON.parse and to stay within typical
 *  localStorage quotas after save. */
export const MAX_IMPORT_BYTES = 50 * 1024 * 1024;

/** Why an import failed. UI maps these to user-visible messages. */
export type ReplayImportError =
  | 'too_large'
  | 'parse_error'
  | 'unsupported_version'
  | 'invalid_structure';

export type ReplayImportResult =
  | { ok: true; replay: ReplayData }
  | { ok: false; reason: ReplayImportError };

export interface ReplayInfo {
  key: string;
  map: string;
  date: string;
  duration: number;
  playerCount: number;
  finalScore: number[];
}

/** Serializable form of a ReplayFrame (Map converted to array of entries). */
interface SerializableFrame {
  tick: number;
  timestamp: number;
  players: [number, any][]; // Map entries as array
  roundPhase: string;
  roundNumber: number;
  teamScores: number[];
  timeRemaining: number;
  bomb: any;
  activeGrenades: any[];
  smokeZones: any[];
  fireZones: any[];
  events: any[];
}

interface SerializableReplay {
  version: number;
  map: string;
  date: string;
  duration: number;
  tickRate: number;
  totalTicks: number;
  frames: SerializableFrame[];
  metadata: {
    players: any[];
    finalScore: number[];
  };
}

export class ReplayStorage {
  private storage: Storage;

  constructor(storage: Storage = localStorage) {
    this.storage = storage;
  }

  /** Save a replay and return the storage key. */
  save(replay: ReplayData): string {
    const key = REPLAY_KEY_PREFIX + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    const serializable = ReplayStorage.toSerializable(replay);
    const json = JSON.stringify(serializable);
    this.storage.setItem(key, json);

    // Update index
    const index = this.getIndex();
    index.push({
      key,
      map: replay.map,
      date: replay.date,
      duration: replay.duration,
      playerCount: replay.metadata.players.length,
      finalScore: replay.metadata.finalScore,
    });
    this.storage.setItem(REPLAY_INDEX_KEY, JSON.stringify(index));

    return key;
  }

  /** Load a replay by key. Returns null if not found or corrupted. */
  load(key: string): ReplayData | null {
    const json = this.storage.getItem(key);
    if (!json) return null;

    try {
      const parsed = JSON.parse(json) as SerializableReplay;
      return ReplayStorage.fromSerializable(parsed);
    } catch {
      return null;
    }
  }

  /** List all saved replays with metadata. */
  list(): ReplayInfo[] {
    const index = this.getIndex();
    // Validate that each entry still exists in storage
    return index.filter(entry => this.storage.getItem(entry.key) !== null);
  }

  /** Delete a saved replay. */
  delete(key: string): void {
    this.storage.removeItem(key);

    // Update index
    const index = this.getIndex();
    const filtered = index.filter(entry => entry.key !== key);
    this.storage.setItem(REPLAY_INDEX_KEY, JSON.stringify(filtered));
  }

  // ── Static serialization helpers (also used by download/import) ──

  /** Convert ReplayData to a JSON-serializable object (Map -> array). */
  static toSerializable(replay: ReplayData): SerializableReplay {
    const frames: SerializableFrame[] = replay.frames.map(frame => ({
      tick: frame.tick,
      timestamp: frame.timestamp,
      players: Array.from(frame.players.entries()),
      roundPhase: frame.roundPhase,
      roundNumber: frame.roundNumber,
      teamScores: frame.teamScores,
      timeRemaining: frame.timeRemaining,
      bomb: frame.bomb,
      activeGrenades: frame.activeGrenades,
      smokeZones: frame.smokeZones,
      fireZones: frame.fireZones,
      events: frame.events,
    }));

    return {
      version: replay.version,
      map: replay.map,
      date: replay.date,
      duration: replay.duration,
      tickRate: replay.tickRate,
      totalTicks: replay.totalTicks,
      frames,
      metadata: replay.metadata,
    };
  }

  /** Deserialize back to ReplayData (array -> Map). */
  static fromSerializable(data: SerializableReplay): ReplayData {
    const frames: ReplayFrame[] = data.frames.map(sf => ({
      tick: sf.tick,
      timestamp: sf.timestamp,
      players: new Map(sf.players),
      roundPhase: sf.roundPhase,
      roundNumber: sf.roundNumber,
      teamScores: sf.teamScores,
      timeRemaining: sf.timeRemaining,
      bomb: sf.bomb,
      activeGrenades: sf.activeGrenades,
      smokeZones: sf.smokeZones,
      fireZones: sf.fireZones,
      events: sf.events,
    }));

    return {
      version: data.version,
      map: data.map,
      date: data.date,
      duration: data.duration,
      tickRate: data.tickRate,
      totalTicks: data.totalTicks,
      frames,
      metadata: data.metadata,
    };
  }

  /** Create a downloadable Blob from replay data. */
  static serializeToBlob(replay: ReplayData): Blob {
    const serializable = ReplayStorage.toSerializable(replay);
    const json = JSON.stringify(serializable);
    return new Blob([json], { type: 'application/json' });
  }

  /** Download a replay as a file. */
  static download(replay: ReplayData, filename: string): void {
    const blob = ReplayStorage.serializeToBlob(replay);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /** Import a replay from a File object. Returns a discriminated result so the
   *  UI can show why a file was rejected (size, version, structure, parse). */
  static async importFromFile(file: File): Promise<ReplayImportResult> {
    if (file.size > MAX_IMPORT_BYTES) {
      return { ok: false, reason: 'too_large' };
    }

    let text: string;
    try {
      text = await file.text();
    } catch {
      return { ok: false, reason: 'parse_error' };
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return { ok: false, reason: 'parse_error' };
    }

    if (!isPlainObject(parsed)) {
      return { ok: false, reason: 'invalid_structure' };
    }

    if ((parsed as { version?: unknown }).version !== REPLAY_VERSION) {
      return { ok: false, reason: 'unsupported_version' };
    }

    if (!isValidReplayShape(parsed)) {
      return { ok: false, reason: 'invalid_structure' };
    }

    try {
      const replay = ReplayStorage.fromSerializable(parsed as unknown as SerializableReplay);
      return { ok: true, replay };
    } catch {
      return { ok: false, reason: 'invalid_structure' };
    }
  }

  // ── Private ──

  private getIndex(): ReplayInfo[] {
    const raw = this.storage.getItem(REPLAY_INDEX_KEY);
    if (!raw) return [];
    try {
      return JSON.parse(raw) as ReplayInfo[];
    } catch {
      return [];
    }
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isValidReplayShape(v: Record<string, unknown>): boolean {
  if (typeof v.map !== 'string' || v.map.length === 0) return false;
  if (typeof v.date !== 'string') return false;
  if (!isFiniteNumber(v.duration)) return false;
  if (!isFiniteNumber(v.tickRate) || (v.tickRate as number) <= 0) return false;
  if (!isFiniteNumber(v.totalTicks)) return false;
  if (!Array.isArray(v.frames)) return false;
  if (!isPlainObject(v.metadata)) return false;
  if (!Array.isArray(v.metadata.players)) return false;
  if (!Array.isArray(v.metadata.finalScore)) return false;
  return true;
}
